import { describeValue } from '../types/taxonomy';
import React, { useState } from 'react';
import { ChevronDownIcon } from '@heroicons/react/24/outline';
import MarkdownText from './MarkdownText';
import {
  CRIMINALIZATION_TYPES,
  CRIMINALIZATION_TRIGGERS,
  LEGAL_INSTRUMENT_TYPES,
  PROCEEDING_TYPES,
  PERPETRATOR_ACCOUNTABILITY_STATUSES,
} from '../types/caseForm';
import { yesNo, yesNoClass } from '../utils/yesNo';
import { enumLabel } from '../utils/enumLabel';

// One card per violation type on the case page (Criminalization, Killing, Other).
//
// Layout: an "At a glance" grid of the coded facts first (form, triggers, proceeding type,
// booleans, accountability), then the narrative grouped by the question a researcher asks —
// What happened / Legal process / Detention and outcome / Perpetrators and accountability /
// State response — each group collapsible with a filled-field count. Coded lists use neutral
// chips; colour is reserved for risk (retaliatory, prior harassment, in detention, no
// accountability). AI marks always precede the value they mark.

export type ViolationKind = 'CRIMINALIZATION' | 'KILLING' | 'OTHER';

interface Props {
  id: string;
  kind: ViolationKind;
  title: string;
  // The ViolationDetail record. May be absent for OTHER when only the case-level "other
  // violation types" are set.
  detail?: Record<string, any> | null;
  // OTHER only: the case-level list and free-text.
  otherViolationTypes?: string[];
  otherViolationTypeDetails?: string;
  aiMark: (field: string | string[]) => React.ReactNode;
  headerExtra?: React.ReactNode;
  // Rendered between the fact grid and the narrative groups (e.g. the per-victim outcomes table).
  extra?: React.ReactNode;
  footer?: React.ReactNode;
}

const optionLabel = (options: { value: string; label: string }[], value: string) =>
  options.find((o) => o.value === value)?.label || value;
const has = (v: unknown) => (Array.isArray(v) ? v.length > 0 : v !== null && v !== undefined && v !== '');

const ACCENT: Record<ViolationKind, string> = {
  CRIMINALIZATION: 'border-orange-500',
  KILLING: 'border-red-600',
  OTHER: 'border-purple-500',
};

// Pills: neutral for facts; red only where the value itself is a risk signal.
const NEUTRAL_CHIP = 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
const GRAY_PILL = NEUTRAL_CHIP;
const RED_PILL = 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300';
// Only "no accountability" is a signal; the rest read as neutral facts.
const ACCOUNTABILITY_PILL: Record<string, string> = {
  NO_INVESTIGATION: RED_PILL,
  STALLED: RED_PILL,
};

// ---- primitives -----------------------------------------------------------------------------

type ChipItem = string | { label: string; title?: string };
const Chips: React.FC<{ items: ChipItem[]; tone?: string; after?: React.ReactNode }> = ({ items, tone = NEUTRAL_CHIP, after }) => (
  <span className="inline-flex flex-wrap gap-1 align-middle">
    {items.map((it) => {
      const label = typeof it === 'string' ? it : it.label;
      const title = typeof it === 'string' ? undefined : it.title;
      return <span key={label} title={title} className={`px-2 py-0.5 rounded text-xs ${tone}`}>{label}</span>;
    })}
    {after}
  </span>
);
// Chips for coded values: the label as the app shows it, the codebook definition on hover.
const coded = (listKey: string, codes: string[], labelOf: (c: string) => string): ChipItem[] =>
  codes.map((c) => ({ label: labelOf(c), title: describeValue(listKey, c) }));

const YesNoPill: React.FC<{ value: boolean | null | undefined; yes?: string; no?: string; yesClass?: string; noClass?: string }> = ({ value, yes = 'Yes', no = 'No', yesClass = GRAY_PILL, noClass = GRAY_PILL }) => (
  <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${yesNoClass(value, yesClass, noClass)}`}>{yesNo(value, yes, no)}</span>
);

const Narrative: React.FC<{ text: string; mark?: React.ReactNode }> = ({ text, mark }) => (
  <MarkdownText text={text} lead={mark} />
);

interface Fact { key: string; label: string; node: React.ReactNode; wide?: boolean }
interface Item { key: string; label: string; node: React.ReactNode }
interface Group { key: string; title: string; items: Item[] }

const FactGrid: React.FC<{ facts: Fact[] }> = ({ facts }) => (
  <dl className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3 rounded-lg bg-gray-50 dark:bg-gray-900/40 p-4">
    {facts.map((f) => (
      <div key={f.key} className={f.wide ? 'col-span-full' : undefined}>
        <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">{f.label}</dt>
        <dd className="mt-1 text-sm text-gray-900 dark:text-gray-100">{f.node}</dd>
      </div>
    ))}
  </dl>
);

const GroupBlock: React.FC<{ group: Group }> = ({ group }) => {
  const [open, setOpen] = useState(true);
  const bodyId = `vg-${group.key}`;
  return (
    <div className="border-t border-gray-200 dark:border-gray-700 pt-3">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="w-full flex items-center justify-between gap-2 text-left"
      >
        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          {group.title}
          <span className="ml-2 text-xs font-normal text-gray-500 dark:text-gray-400">{group.items.length} field{group.items.length === 1 ? '' : 's'}</span>
        </span>
        <ChevronDownIcon className={`h-4 w-4 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {open && (
        <div id={bodyId} className="mt-3 space-y-3">
          {group.items.map((it) => (
            <div key={it.key}>
              <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">{it.label}</h4>
              {it.node}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ---- card -----------------------------------------------------------------------------------

const ViolationDetailCard: React.FC<Props> = ({ id, kind, title, detail, otherViolationTypes, otherViolationTypeDetails, aiMark, headerExtra, extra, footer }) => {
  const d = detail ?? null;
  const path = (field: string) => `violationDetails[${d?.id}].${field}`;

  // ---------- at a glance ----------
  const facts: Fact[] = [];
  const fact = (key: string, label: string, node: React.ReactNode, wide = false) => facts.push({ key, label, node, wide });

  if (kind === 'OTHER' && has(otherViolationTypes)) fact('types', 'Violation types', <Chips items={coded('violationTypes', otherViolationTypes!, enumLabel)} />, true);
  if (kind === 'CRIMINALIZATION' && d) {
    if (has(d.criminalizationTypes)) fact('form', 'Form of criminalization', <Chips items={coded('criminalizationTypes', d.criminalizationTypes, (t) => optionLabel(CRIMINALIZATION_TYPES, t))} />, true);
    if (has(d.criminalizationTriggers)) fact('triggers', 'Criminalized activities', <Chips items={coded('criminalizationTriggers', d.criminalizationTriggers, (t) => optionLabel(CRIMINALIZATION_TRIGGERS, t))} />, true);
    if (d.proceedingType) fact('proceeding', 'Type of proceeding', <Chips items={coded('proceedingType', [d.proceedingType], (t) => optionLabel(PROCEEDING_TYPES, t))} />);
    if (has(d.legalInstrumentTypes)) fact('instruments', 'Legal instruments', <Chips items={coded('legalInstrumentTypes', d.legalInstrumentTypes, (t) => optionLabel(LEGAL_INSTRUMENT_TYPES, t))} />, true);
    if (d.isRetaliatoryProceeding != null) fact('retaliatory', 'Retaliatory proceeding', <YesNoPill value={d.isRetaliatoryProceeding} yesClass={RED_PILL} />);
    if (has(d.whoFiledAccusations)) fact('whoFiled', 'Who filed accusations', <Chips items={coded('perpetratorTypes', d.whoFiledAccusations, enumLabel)} />, true);
    if (d.accusationsLegallyFiled !== undefined) fact('filed', 'Formally filed', <YesNoPill value={d.accusationsLegallyFiled} />);
    if (d.decisionOnAccusations !== undefined) fact('decision', 'Decision reached', <YesNoPill value={d.decisionOnAccusations} />);
    if (d.victimsInPrison !== undefined) fact('prison', 'In prison / detention', <YesNoPill value={d.victimsInPrison} yes="Yes, in detention" no="Not in detention" yesClass={RED_PILL} />);
  }
  if (kind !== 'OTHER' && d && d.harassmentPrior !== undefined) fact('harassment', kind === 'KILLING' ? 'Harassment before the killing' : 'Harassment before criminalization', <YesNoPill value={d.harassmentPrior} yesClass={RED_PILL} />);
  if (d && d.perpetratorKnown !== undefined) fact('perpKnown', 'Perpetrators known', <YesNoPill value={d.perpetratorKnown} />);
  if (d && has(d.perpetratorTypes)) fact('perpTypes', 'Perpetrator types', <Chips items={coded('perpetratorTypes', d.perpetratorTypes, enumLabel)} after={aiMark(path('perpetratorTypes'))} />, true);
  if (d?.perpetratorAccountabilityStatus) {
    fact('accountability', 'Accountability', <span title={describeValue('perpetratorAccountabilityStatus', d.perpetratorAccountabilityStatus)} className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${ACCOUNTABILITY_PILL[d.perpetratorAccountabilityStatus] || GRAY_PILL}`}>{optionLabel(PERPETRATOR_ACCOUNTABILITY_STATUSES, d.perpetratorAccountabilityStatus)}</span>);
  }
  if (d && d.legalCaseFiled !== undefined) fact('legalCase', 'Legal case filed', <YesNoPill value={d.legalCaseFiled} />);
  if (d && d.stateActionUndertaken !== undefined) fact('stateAction', 'State action undertaken', <YesNoPill value={d.stateActionUndertaken} />);

  // ---------- grouped narrative ----------
  const groups: Group[] = [];
  const group = (key: string, title: string, items: Array<Item | null | false>) => {
    const kept = items.filter((x): x is Item => Boolean(x));
    if (kept.length) groups.push({ key, title, items: kept });
  };
  const text = (key: string, label: string, value: string | null | undefined, markPath?: string): Item | null =>
    value ? { key, label, node: <Narrative text={value} mark={markPath ? aiMark(markPath) : undefined} /> } : null;

  if (kind === 'CRIMINALIZATION' && d) {
    group('what', 'What happened', [
      text('why', 'Why the victims were criminalized', d.whyVictimsCriminalized, path('whyVictimsCriminalized')),
      text('harassmentDetails', 'Harassment before criminalization', d.harassmentPriorDetails, path('harassmentPriorDetails')),
    ]);
    group('legal', 'Legal process', [
      text('accusations', 'Accusations / charges filed', d.accusations, path('accusations')),
      text('laws', 'Policies or laws used', d.policiesOrLawsUsed, path('policiesOrLawsUsed')),
      text('whoFiledDetails', 'Who filed the accusations', d.whoFiledAccusationsDetails),
      text('court', 'Court location', d.courtFilingLocation, path('courtFilingLocation')),
      text('courtDetails', 'Filing details', d.courtFilingDetails, path('courtFilingDetails')),
      text('decisionDetails', 'Decision on the accusations', d.decisionDetails),
    ]);
    group('detention', 'Detention and outcome', [
      text('prisonConditions', 'Prison conditions', d.prisonConditions, path('prisonConditions')),
      text('situation', 'Current situation of the victims', d.victimsSituationIfNotInPrison, path('victimsSituationIfNotInPrison')),
    ]);
  }
  if (kind === 'KILLING' && d) {
    group('what', 'What happened', [
      text('why', 'Why the victims were killed', d.whyVictimsKilled, path('whyVictimsKilled')),
      text('how', 'How the killing was carried out', d.howKillingCarriedOut, path('howKillingCarriedOut')),
      text('harassmentDetails', 'Harassment before the killing', d.harassmentPriorDetails, path('harassmentPriorDetails')),
    ]);
  }
  if (kind === 'OTHER') {
    group('what', 'What happened', [
      text('otherSpecified', 'Other violation (specified)', otherViolationTypeDetails, 'otherViolationTypeDetails'),
      d ? text('cause', 'Cause', d.cause, path('cause')) : null,
    ]);
  }
  if (d) {
    group('perpetrators', 'Perpetrators and accountability', [
      text('perpDetails', 'Alleged perpetrators', d.perpetratorDetails, path('perpetratorDetails')),
    ]);
    group('state', 'State response', [
      d.legalCaseFiled ? text('legalCaseDetails', 'Legal case', d.legalCaseDetails, path('legalCaseDetails')) : null,
      !d.legalCaseFiled ? text('legalCaseWhyNot', 'Why no legal case was filed', d.legalCaseWhyNot, path('legalCaseWhyNot')) : null,
      d.stateActionUndertaken ? text('stateActionDetails', 'State action', d.stateActionDetails, path('stateActionDetails')) : null,
      !d.stateActionUndertaken ? text('stateActionWhyNot', 'Why no state action was taken', d.stateActionWhyNot, path('stateActionWhyNot')) : null,
    ]);
  }

  return (
    <div id={id} className={`bg-white dark:bg-gray-800 shadow rounded-lg p-6 border-l-4 ${ACCENT[kind]}`}>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-4">
        {title}{headerExtra}
      </h2>
      <div className="space-y-4">
        {facts.length > 0 && (
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-2">At a glance</h3>
            <FactGrid facts={facts} />
          </div>
        )}
        {extra}
        {groups.map((g) => <GroupBlock key={g.key} group={g} />)}
        {facts.length === 0 && groups.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400">No details recorded for this violation.</p>
        )}
        {footer}
      </div>
    </div>
  );
};

export default ViolationDetailCard;
